import { AlertTriangle, Clock, Linkedin, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PortalListNote } from '@/modules/hiring-manager/components/PortalList';
import { Button } from '@/modules/ui/components/button/Button';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';

import {
  candidateName,
  type FailureReason,
  type MessageApi,
  type MessageThreadApi,
  reasonCopy,
} from './outreachThread';

/** Candidate names shown before the group collapses into "and N more". */
const NAMES_SHOWN = 4;

export interface AttentionGroup {
  reason: FailureReason;
  threads: MessageThreadApi[];
  messages: MessageApi[];
}

interface MessagesAttentionProps {
  groups: AttentionGroup[];
  integrationsHref?: string;
  isPending: boolean;
  isResending: boolean;
  onResend: (messages: MessageApi[]) => void;
}

/**
 * Problems first: everything that failed, grouped by why, each group with
 * the one action that resolves it. The log below is for browsing.
 */
export function MessagesAttention({
  groups,
  integrationsHref,
  isPending,
  isResending,
  onResend,
}: MessagesAttentionProps) {
  if (isPending) {
    return (
      <div className="divide-border divide-y border-y">
        <Skeleton className="my-5 h-10 w-full" />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <PortalListNote>
        Everything sent. Finlay posts here and in Slack if a message fails.
      </PortalListNote>
    );
  }

  return (
    <ul className="divide-border divide-y border-y">
      {groups.map((group) => (
        <AttentionRow
          group={group}
          integrationsHref={integrationsHref}
          isResending={isResending}
          key={group.reason}
          onResend={onResend}
        />
      ))}
    </ul>
  );
}

function AttentionRow({
  group,
  integrationsHref,
  isResending,
  onResend,
}: {
  group: AttentionGroup;
  integrationsHref?: string;
  isResending: boolean;
  onResend: (messages: MessageApi[]) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const copy = reasonCopy[group.reason];
  const shown = isExpanded
    ? group.threads
    : group.threads.slice(0, NAMES_SHOWN);
  const hidden = group.threads.length - shown.length;

  return (
    <li className="grid gap-x-6 gap-y-3 py-4 md:grid-cols-[minmax(0,1fr)_auto]">
      <div className="flex min-w-0 flex-col gap-1.5">
        <p className="flex items-center gap-2 font-medium">
          {copy.tone === 'warn' ? (
            <AlertTriangle aria-hidden className="size-4 text-red-600" />
          ) : (
            <Clock aria-hidden className="size-4 text-neutral-500" />
          )}
          {copy.title}
          <span className="text-[13px] font-medium text-neutral-500 tabular-nums">
            {group.threads.length}
          </span>
        </p>
        <p className="text-muted-foreground max-w-[64ch] text-[13px] leading-[18px]">
          {copy.hint}
        </p>
        <p className="flex flex-wrap items-center gap-x-1.5 text-[13px] text-neutral-500">
          {shown.map((thread, index) => (
            <span key={thread.id}>
              {candidateName(thread)}
              {index < shown.length - 1 && ','}
            </span>
          ))}
          {hidden > 0 && (
            <button
              className="hover:text-foreground cursor-pointer"
              onClick={() => setIsExpanded(true)}
              type="button"
            >
              and {hidden} more
            </button>
          )}
        </p>
      </div>
      <div className="flex items-start md:justify-end">
        {copy.action === 'reconnect' &&
          (integrationsHref ? (
            <Button asChild className="h-7 rounded-md" size="sm">
              <Link to={integrationsHref}>
                <Linkedin className="size-3.5" />
                Reconnect LinkedIn
              </Link>
            </Button>
          ) : (
            <p className="text-muted-foreground max-w-[24ch] text-[13px] leading-[18px]">
              LinkedIn setup is not available for this workspace.
            </p>
          ))}
        {copy.action === 'resend' && (
          <Button
            className="h-7 rounded-md"
            loading={isResending}
            onClick={() => onResend(group.messages)}
            size="sm"
            variant="outline"
          >
            <RefreshCw className="size-3.5" />
            Resend {group.threads.length}
          </Button>
        )}
      </div>
    </li>
  );
}
